import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, Mail, CheckCircle, ArrowLeft, Key, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useOrganizationBranding } from '@/hooks/useOrganizationBranding';
import { UnifiedLanguageSwitcher } from '@/components/common/UnifiedLanguageSwitcher';
import { buildAbsoluteUrl } from '@/lib/basePath';

const createEmailSchema = (t: any) => z.object({
  email: z.string().email(t('auth.email.invalid')),
});

const createCodeSchema = (t: any) => z.object({
  code: z.string().length(6, t('auth.verifyCode.invalid')),
});

type EmailForm = {
  email: string;
};

type CodeForm = {
  code: string;
};

interface RequestState {
  status: 'idle' | 'loading' | 'success' | 'error' | 'code_sent';
  message?: string;
  expiresAt?: Date;
  isVerifying?: boolean;
}

interface PortalLoginProps {
  onSuccess: (sessionData: any) => void;
}

export function PortalLogin({ onSuccess }: PortalLoginProps) {
  const { t } = useTranslation('portal');
  const [requestState, setRequestState] = useState<RequestState>({ status: 'idle' });
  const [activeTab, setActiveTab] = useState('code');
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const { name: orgName, logoUrl, brandColorHex } = useOrganizationBranding();

  const emailForm = useForm<EmailForm>({
    resolver: zodResolver(createEmailSchema(t)),
    defaultValues: { email: '' },
  });

  const codeForm = useForm<CodeForm>({
    resolver: zodResolver(createCodeSchema(t)),
    defaultValues: { code: '' },
  });

  // Countdown timer for resend button
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  // Apply brand color
  useEffect(() => {
    if (brandColorHex) {
      document.documentElement.style.setProperty('--brand-color', brandColorHex);
    }
  }, [brandColorHex]);

  const extractEdgeFunctionError = async (error: any): Promise<string | null> => {
    try {
      // Try parsing error.context as Response (FunctionsHttpError)
      if (error?.context instanceof Response) {
        const cloned = error.context.clone();
        const errorData = await cloned.json();
        return errorData?.error || null;
      }
      // Try parsing error.context as already-parsed object
      if (error?.context?.error) {
        return error.context.error;
      }
      // Try parsing the error message itself if it contains JSON
      if (error?.message) {
        const jsonMatch = error.message.match(/\{.*\}/s);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0]);
          return parsed?.error || null;
        }
      }
    } catch {
      // If parsing fails, return null
    }
    return null;
  };

  const mapErrorToMessage = (errorMsg: string): string => {
    const lower = errorMsg.toLowerCase();
    if (lower.includes('customer not found') || lower.includes('no customer account')) {
      return t('auth.errors.customerNotFound');
    }
    if (lower.includes('rate limit') || lower.includes('account locked')) {
      return t('auth.errors.rateLimited');
    }
    if (lower.includes('otp') && lower.includes('not enabled')) {
      return t('auth.errors.otpDisabled');
    }
    if (lower.includes('invalid') || lower.includes('incorrect')) {
      return t('auth.verifyCode.invalid');
    }
    if (lower.includes('expired')) {
      return t('auth.verifyCode.expired');
    }
    return errorMsg;
  };

  const sendCode = async (data: EmailForm) => {
    try {
      setRequestState({ status: 'loading' });

      const { data: response, error } = await supabase.functions.invoke('portal-auth-send-code', {
        body: { 
          email: data.email,
          delivery: 'email'
        }
      });

      if (error) {
        console.error('Send code error:', error);
        const edgeError = await extractEdgeFunctionError(error);
        const errorMessage = edgeError ? mapErrorToMessage(edgeError) : t('auth.errors.generic');
        setRequestState({ status: 'error', message: errorMessage });
        return;
      }

      if (response?.success) {
        const expiresAt = new Date(Date.now() + (response.expiresIn * 1000));
        
        // Clear the code form when a new code is sent
        codeForm.reset();
        
        setRequestState({
          status: 'code_sent',
          message: t('auth.verifyCode.subtitle', { email: data.email }),
          expiresAt
        });
        setCountdown(30); // 30 second cooldown before resend
        setCanResend(false);
        
        // Store email for verification
        localStorage.setItem('portal_auth_email', data.email);
      } else {
        setRequestState({
          status: 'error',
          message: 'Failed to send login code. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('Send code error:', error);
      setRequestState({
        status: 'error',
        message: 'An unexpected error occurred. Please try again.'
      });
    }
  };

  const verifyCode = async (data: CodeForm) => {
    // Store the current code value to preserve it on error
    const currentCode = data.code;
    
    try {
      setRequestState(prev => ({ ...prev, isVerifying: true }));
      const email = localStorage.getItem('portal_auth_email') || emailForm.getValues().email;

      const { data: response, error } = await supabase.functions.invoke('portal-auth-verify-code', {
        body: { 
          email,
          code: currentCode
        }
      });

      if (error) {
        console.error('Verify code error:', error);
        setTimeout(() => { codeForm.setValue('code', currentCode); }, 0);
        const edgeError = await extractEdgeFunctionError(error);
        const errorMessage = edgeError ? mapErrorToMessage(edgeError) : t('auth.errors.generic');
        setRequestState({ status: 'error', message: errorMessage, isVerifying: false });
        return;
      }

      if (response?.success) {
        // Store session data
        localStorage.setItem('customer_session', JSON.stringify(response.session));
        localStorage.removeItem('portal_auth_email');
        
        console.log('Code verification successful, session stored:', response.session);
        
        // Trigger manual session check in CustomerAuthProvider
        window.dispatchEvent(new CustomEvent('portalSessionUpdated'));
        
        setRequestState({
          status: 'success',
          message: 'Successfully signed in!',
          isVerifying: false
        });

        // Call success callback
        onSuccess(response.session);
      } else {
        // Handle case where response exists but success is false
        // Preserve the code value by ensuring it stays in the form
        setTimeout(() => {
          codeForm.setValue('code', currentCode);
        }, 0);
        const msg = response?.error ? mapErrorToMessage(response.error) : t('auth.errors.generic');
        setRequestState({
          status: 'error',
          message: msg,
          isVerifying: false
        });
      }
    } catch (error: any) {
      console.error('Verify code error:', error);
      // Preserve the code value by ensuring it stays in the form
      setTimeout(() => {
        codeForm.setValue('code', currentCode);
      }, 0);
      setRequestState({
        status: 'error',
        message: t('auth.errors.generic'),
        isVerifying: false
      });
    }
  };

  const sendMagicLink = async (data: EmailForm) => {
    try {
      setRequestState({ status: 'loading' });

      const { data: response, error } = await supabase.functions.invoke('generate-magic-link', {
        body: { 
          email: data.email,
          expiryMinutes: 30,
          redirectUrl: buildAbsoluteUrl('/')
        }
      });

      if (error) {
        console.error('Magic link error:', error);
        setRequestState({
          status: 'error',
          message: error.message || 'Failed to send magic link. Please try again.'
        });
        return;
      }

      if (response?.success) {
        setRequestState({
          status: 'success',
          message: t('magicLinkRequest.success.message', { email: data.email })
        });
      } else {
        setRequestState({
          status: 'error',
          message: 'Failed to send magic link. Please try again.'
        });
      }
    } catch (error: any) {
      console.error('Magic link error:', error);
      setRequestState({
        status: 'error',
        message: 'An unexpected error occurred. Please try again.'
      });
    }
  };

  const handleReset = () => {
    setRequestState({ status: 'idle' });
    emailForm.reset();
    codeForm.reset();
    setCountdown(0);
    setCanResend(false);
    localStorage.removeItem('portal_auth_email');
  };

  const handleResend = () => {
    const email = localStorage.getItem('portal_auth_email') || emailForm.getValues().email;
    if (email) {
      // Clear the code form before sending a new code
      codeForm.reset();
      sendCode({ email });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (requestState.status === 'success' && activeTab === 'magic') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/50 to-muted/30 flex items-center justify-center p-4">
        <UnifiedLanguageSwitcher variant="floating" />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
            <div className="flex justify-center">
              {logoUrl ? (
                <img src={logoUrl} alt={orgName} className="h-16 w-16 object-contain rounded-lg" />
              ) : (
                <div className="bg-green-100 rounded-full p-3">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              )}
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-green-800">{t('magicLink.sent.title')}</CardTitle>
              <CardDescription className="text-base mt-2">
                {t('magicLink.sent.subtitle')}
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <Mail className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {requestState.message}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Button onClick={handleReset} variant="outline" className="w-full">
                {t('magicLinkRequest.success.sendAnotherButton')}
              </Button>
              <Button variant="ghost" className="w-full" onClick={handleReset}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('magicLinkRequest.success.tryAgainButton')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/50 to-muted/30 flex items-center justify-center p-4">
      <UnifiedLanguageSwitcher variant="floating" />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            {logoUrl ? (
              <img src={logoUrl} alt={orgName} className="h-16 w-16 object-contain rounded-lg" />
            ) : (
              <img 
                src="/lovable-uploads/4c245772-e039-4a52-bd56-9e693e02f0af.png" 
                alt={orgName || 'Organization logo'}
                className="h-16 w-16 object-contain rounded-lg"
              />
            )}
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">{t('auth.title')}</CardTitle>
            <CardDescription className="text-base mt-2">
              {t('auth.subtitle')}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-1 md:grid-cols-2 w-full h-auto md:h-10 bg-muted p-1 rounded-md">
              <TabsTrigger value="code" className="flex items-center justify-center gap-2 w-full h-12 md:h-8 text-sm font-medium rounded-sm transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                <Key className="h-4 w-4 flex-shrink-0" />
                <span>{t('tabs.loginCode')}</span>
              </TabsTrigger>
              <TabsTrigger 
                value="magic" 
                disabled
                className="flex items-center justify-center gap-2 w-full h-12 md:h-8 text-sm font-medium rounded-sm transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Mail className="h-4 w-4 flex-shrink-0" />
                <span>{t('tabs.magicLink')}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="code" className="space-y-4">
              {requestState.status !== 'code_sent' ? (
                <Form {...emailForm}>
                  <form onSubmit={emailForm.handleSubmit(sendCode)} className="space-y-4">
                    <FormField
                      control={emailForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('auth.email.label')}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={t('auth.email.placeholder')}
                              type="email"
                              disabled={requestState.status === 'loading'}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      className="w-full" 
                      disabled={requestState.status === 'loading'}
                    >
                      {requestState.status === 'loading' ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {t('auth.sendCode.sending')}
                        </>
                      ) : (
                        <>
                          <Key className="h-4 w-4 mr-2" />
                          {t('auth.sendCode.button')}
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              ) : (
                <div className="space-y-4">
                  <Alert className="border-blue-200 bg-blue-50">
                    <Mail className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      {requestState.message}
                    </AlertDescription>
                  </Alert>

                  <Form {...codeForm}>
                    <form onSubmit={codeForm.handleSubmit(verifyCode)} className="space-y-4">
                      <FormField
                        control={codeForm.control}
                        name="code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('auth.verifyCode.label')}</FormLabel>
                            <FormControl>
                              <InputOTP
                                maxLength={6}
                                value={field.value}
                                onChange={field.onChange}
                                disabled={requestState.status === 'loading'}
                              >
                                <InputOTPGroup>
                                  <InputOTPSlot index={0} />
                                  <InputOTPSlot index={1} />
                                  <InputOTPSlot index={2} />
                                  <InputOTPSlot index={3} />
                                  <InputOTPSlot index={4} />
                                  <InputOTPSlot index={5} />
                                </InputOTPGroup>
                              </InputOTP>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="flex gap-2">
                        <Button 
                          type="submit" 
                          className="flex-1" 
                          disabled={requestState.isVerifying || codeForm.watch('code')?.length !== 6}
                        >
                          {requestState.isVerifying ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              {t('auth.verifyCode.verifying')}
                            </>
                          ) : (
                            t('auth.verifyCode.button')
                          )}
                        </Button>
                        
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={canResend ? handleResend : undefined}
                          disabled={!canResend || requestState.isVerifying}
                          className="flex-1"
                        >
                          {canResend ? (
                            t('auth.sendCode.resend')
                          ) : (
                            <>
                              <Clock className="h-4 w-4 mr-2" />
                              {t('auth.sendCode.resendIn', { seconds: countdown })}
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </Form>

                  <Button 
                    variant="ghost" 
                    onClick={handleReset}
                    className="w-full"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {t('auth.changeEmail')}
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="magic" className="space-y-4">
              <Form {...emailForm}>
                <form onSubmit={emailForm.handleSubmit(sendMagicLink)} className="space-y-4">
                  <FormField
                    control={emailForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('magicLink.emailLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            placeholder={t('magicLink.emailPlaceholder')}
                            type="email"
                            disabled={requestState.status === 'loading'}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={requestState.status === 'loading'}
                  >
                    {requestState.status === 'loading' ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('magicLinkRequest.sendingButton')}
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        {t('magicLinkRequest.sendButton')}
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </TabsContent>
          </Tabs>

          {requestState.status === 'error' && requestState.message && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>
                {requestState.message}
              </AlertDescription>
            </Alert>
          )}

          <div className="mt-6 text-center">
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">
                {t('security.info')}
                <br />
                {t('security.expiry')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}